import { useEffect, useRef, useState, useCallback } from 'react';

export function useWebSocket(url) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [backendReachable, setBackendReachable] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const healthCheck = useCallback(async () => {
    try {
      // Derive backend HTTP(S) base from the provided URL (which may include /api/ws or ws/wss)
      let base;
      try {
        const u = new URL(url);
        base = u.origin;
        // Convert ws/wss -> http/https for fetch
        if (base.startsWith('ws:')) base = base.replace(/^ws:/, 'http:');
        if (base.startsWith('wss:')) base = base.replace(/^wss:/, 'https:');
      } catch {
        // Fallback: strip any trailing path segments and replace scheme
        base = url.replace(/\/ws\/?$/, '').replace(/\/$/, '');
        base = base.replace(/^wss:\/\//, 'https://').replace(/^ws:\/\//, 'http://').replace(/^\/\/?/, '');
        if (!/^https?:\/\//.test(base)) base = 'http://' + base;
      }

      const healthUrl = `${base.replace(/\/$/, '')}/api/system/health`;
      const res = await fetch(healthUrl, { cache: 'no-store' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        return { ok: false, status: res.status, body: text };
      }
      return { ok: true, status: res.status };
    } catch (err) {
      return { ok: false, error: String(err) };
    }
  }, [url]);

  const connect = useCallback(() => {
    // Prevent multiple connections
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Stop if we've decided to stop reconnecting
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.warn('Max WebSocket reconnect attempts reached');
      return;
    }

    const wsUrl = url.replace('https://', 'wss://').replace('http://', 'ws://');
    console.info(`📡 Connecting to WebSocket: ${wsUrl} (attempt ${reconnectAttemptsRef.current + 1})`);
    
    try {
      wsRef.current = new WebSocket(wsUrl);
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setBackendReachable(false);
      setErrorMessage(`Malformed WebSocket URL or environment issue: ${String(error)}`);
      return;
    }

    wsRef.current.onopen = () => {
      console.info('✅ WebSocket connected');
      setIsConnected(true);
      setBackendReachable(true);
      setErrorMessage(null);
      reconnectAttemptsRef.current = 0; // reset attempts on success

      // Keepalive ping
      const pingInterval = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          try { wsRef.current.send('ping'); } catch {}
        }
      }, 30000);
      wsRef.current.pingInterval = pingInterval;
    };

    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setLastMessage(data);
      } catch {
        // ignore non-json messages
      }
    };

    wsRef.current.onerror = async (ev) => {
      console.error('WebSocket error event:', ev);

      // Probe backend health to decide whether to keep retrying
      const probe = await healthCheck();
      if (!probe.ok) {
        const details = probe.error || `status=${probe.status}` + (probe.body ? ` body=${probe.body}` : '');
        console.error('Backend health check failed:', details);
        setBackendReachable(false);
        setErrorMessage(`Backend unreachable: ${details}`);
        // Stop further reconnect attempts
        reconnectAttemptsRef.current = maxReconnectAttempts;
      } else {
        console.info('Backend reachable but WebSocket errored; will attempt reconnect.');
        setBackendReachable(true);
        setErrorMessage(null);
      }

      try { wsRef.current?.close(); } catch {}
    };

    wsRef.current.onclose = (event) => {
      console.info(`🔌 WebSocket disconnected (code=${event?.code}, reason=${event?.reason || 'none'})`);
      setIsConnected(false);

      if (wsRef.current?.pingInterval) {
        clearInterval(wsRef.current.pingInterval);
      }

      // No reconnect if we've exhausted attempts or backend is known unreachable
      if (reconnectAttemptsRef.current >= maxReconnectAttempts || backendReachable === false) {
        console.info('Reconnect halted (max attempts reached or backend unreachable).');
        return;
      }

      // Exponential backoff for reconnect attempts
      reconnectAttemptsRef.current += 1;
      const delay = Math.min(1000 * 2 ** (reconnectAttemptsRef.current - 1), 15000);
      reconnectTimeoutRef.current = setTimeout(connect, delay);
      console.info(`Scheduled reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
    };
  }, [url, healthCheck, backendReachable]);

  useEffect(() => {
    connect();

    return () => {
      console.info('Cleaning up WebSocket');
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (wsRef.current?.pingInterval) clearInterval(wsRef.current.pingInterval);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
        wsRef.current = null;
      }
    };
  }, [connect]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof message === 'string' ? message : JSON.stringify(message));
    } else {
      console.warn('Cannot send message: WebSocket is not connected');
    }
  }, []);

  return { isConnected, lastMessage, sendMessage, backendReachable, errorMessage };
}
